//
// TextInput
//
// autoFill & fillType set in turn the html type and autoComplete parameters.
// name might be needed for autocomplete to work!
//
//  fillType:           html type:            html autoComplete:
//
//  fullName            text                  name
//  firstName           text                  given-name
//  lastName            text                  family-name
//  middleName          text                  additional-name
//  namePrefix          text                  honorific-prefix
//  nameSuffix          text                  honorific-suffix
//  username            text                  username
//  password            password              password
//  email               email                 email
//  phone               tel                   tel
//  creditCardNumber    text                  cc-number
//  creditCardExp       text                  cc-exp
//  creditCardCode      text                  cc-csc
//  creditCardHolder    text                  cc-name
//  streetAddress       text                  street-address
//  address1            text                  address-line1
//  address2            text                  address-line2
//  city                text                  address-level2
//  state               text                  address-level1
//  postCode            text                  postal-code
//  country             text                  country
//  disabled            search                off
//  url                 url                   url
//  birthday            date                  bday
//  off                 search                off
//  storedPassword      password              current-password
//  newPassword         password              new-password
//  oneTimeCode         text                  otp
//
//
// autoComplete must be:
// off, on, name, honorific-prefix, given-name, additional-name, family-name,
// honorific-suffix, nickname, email, username, new-password,
// one-time-code, organization-title, organization, street-address,
// address-line1, address-line2, address-line3, address-level4, address-level3,
// address-level2, address-level1,
// country, country-name, postal-code,
// cc-name, cc-given-name, cc-additional-name, cc-family-name, cc-number,
// cc-exp, cc-exp-month, cc-exp-year, cc-csc, cc-type,
// transaction-currency, transaction-amount, language,
// bday, bday-day, bday-month, bday-year, sex, tel,
// tel-country-code, tel-national, tel-area-code, tel-local,
// tel-extension, impp, url, photo,
//
//
// type is one of (https://www.w3schools.com/TAgs/att_input_type.asp):
// email           Defines a field for an e-mail address
// number          Defines a field for entering a number
// password        Defines a password field
// tel             Defines a field for entering a telephone number
// text            Default. Defines a single-line text field
// url             Defines a field for entering a URL
//
// Other possibilities (not used here):
// button          Defines a clickable button (mostly used with a JavaScript to activate a script)
// checkbox        Defines a checkbox
// color           Defines a color picker
// date            Defines a date control (year, month, day (no time))
// datetime-local  Defines a date and time control (year, month, day, time (no timezone)
// file            Defines a file-select field and a "Browse" button (for file uploads)
// hidden          Defines a hidden input field
// image           Defines an image as the submit button
// month           Defines a month and year control (no timezone)
// radio           Defines a radio button
// range           Defines a range control (like a slider control)
// reset           Defines a reset button
// search          Defines a text field for entering a search string
// submit          Defines a submit button
// time            Defines a control for entering a time (no timezone)
// week            Defines a week and year control (no timezone)
//
import classNames from 'classnames';
import PropTypes from 'prop-types';
import {
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';

import {
  useDisplay,
  useTownAssets,
} from '../../stores';

// The on-screen keyboard animates in after the focus event, so a field brought into view on
// focus has to be brought back once the keyboard has settled
const keyboardSettleMs = 800;

const fillTypeToAutoComplete = {
  fullName: 'name',
  firstName: 'given-name',
  lastName: 'family-name',
  middleName: 'additional-name',
  namePrefix: 'honorific-prefix',
  nameSuffix: 'honorific-suffix',
  username: 'username',
  password: 'password', // NOSONAR
  email: 'email',
  phone: 'tel',
  creditCardNumber: 'cc-number',
  creditCardExp: 'cc-exp',
  creditCardCode: 'cc-csc',
  creditCardHolder: 'cc-name',
  streetAddress: 'street-address',
  address1: 'address-line1',
  address2: 'address-line2',
  city: 'address-level2',
  state: 'address-level1',
  postCode: 'postal-code',
  country: 'country',
  disabled: 'off',
  url: 'url',
  birthday: 'bday',
  off: 'off',
  storedPassword: 'current-password', // NOSONAR
  newPassword: 'new-password', // NOSONAR
  oneTimeCode: 'otp',
};

const fillTypeToType = {
  fullName: 'text',
  firstName: 'text',
  lastName: 'text',
  middleName: 'text',
  namePrefix: 'text',
  nameSuffix: 'text',
  username: 'text',
  password: 'password', // NOSONAR
  email: 'email',
  phone: 'tel',
  creditCardNumber: 'text',
  creditCardExp: 'text',
  creditCardCode: 'text',
  creditCardHolder: 'text',
  streetAddress: 'text',
  address1: 'text',
  address2: 'text',
  city: 'text',
  state: 'text',
  postCode: 'text',
  country: 'text',
  disabled: 'search',
  url: 'url',
  birthday: 'date',
  off: 'search',
  storedPassword: 'password', // NOSONAR
  newPassword: 'password', // NOSONAR
  oneTimeCode: 'text',
};
//
// Word processors, phones and pasted documents produce characters the text patterns
// refuse; these are the straight equivalents those patterns accept
//
const typographicCharacters = [
  [/[\u2018\u2019\u201A\u201B]/g, '\''],
  [/[\u201C\u201D\u201E\u00AB\u00BB]/g, '"'],
  [/\u2026/g, '...'],
  [/[\u2013\u2010\u2011\u2012]/g, '-'],
];

const normalizeTypedText = (value) => typographicCharacters.reduce(
  (text, [from, to]) => text.replace(from, to),
  value,
);

const TextInput = function TextInput(props) {
  const {
    className,
    style,
    id,
    placeholder = '',
    multiline,
    onChange,
    onKeyPress,
    onKeyDown,
    onBlur,
    value,
    hideLabel,
    fullWidth,
    name,
    fillType,
    autoFill,
    invalid,
    nullText,
    showPassword,
    focus,
    widthChars,
    disabled,
    optional,
    required,
    nonEditablePrefix,
    onFocusUnfocus,
    customTxtColor,
    customMaxHeight,
    spellCheck,
    noScrollIntoView,
  } = props;
  const inputRef = useRef(null);
  const inputShowPwdRef = useRef(null);
  const isMountedRef = useRef(false);
  const { display } = useDisplay(false);
  const narrow = Boolean(display?.narrow);
  const { assets } = useTownAssets();
  const {
    translate,
  } = assets;

  const autoComplete = autoFill
    ? fillTypeToAutoComplete[fillType]
    : 'off';

  const type = fillTypeToType[fillType];

  const divClassName = useMemo(
    () => classNames(
      className,
      // min-w-0 lets the bordered container shrink in a flex row instead of
      // holding its content width and pushing siblings (e.g. an info button) off
      'flex flex-row relative rounded-md border min-w-0',
      {
        // 'overflowy-scroll': multiline,
        'w-full': fullWidth,
        'border-pxMainOutline': (!invalid),
        'border-pxUiAlert': invalid,
        'opacity-80': disabled,
      },
    ),
    [className, fullWidth, invalid, disabled],
  );

  const txtClassName = useMemo(
    () => classNames(
      'outline-none resize-none overflow-auto bg-transparent py-2 pr-2',
      {
        'text-black placeholder:text-black/40': (!invalid) && (!customTxtColor),
        'text-pxUiAlert placeholder:text-pxUiAlert/50': invalid && (!customTxtColor),
        'leading-4 truncate p-0': (!multiline),
        'min-h-[50px]': multiline,
        'max-h-[100px]': multiline && (!customMaxHeight),
        // min-w-0 lets the field shrink in a flex row so truncate engages
        // instead of the intrinsic text width forcing the line to overflow
        'flex-1 min-w-0': (!widthChars),
        'pl-2': !nonEditablePrefix?.length,
        'pointer-events-none': disabled,
      },
    ),
    [
      multiline, invalid, widthChars, customTxtColor, customMaxHeight,
      nonEditablePrefix?.length, disabled,
    ],
  );

  const labelClassName = useMemo(
    () => classNames(
      'absolute -top-2 left-2 px-1 text-xs bg-white text-nowrap',
      {
        'text-black/60 ': !invalid,
        'text-pxUiAlert ': invalid,
      },
    ),
    [invalid],
  );

  const txtStyle = useMemo(
    () => {
      if (!widthChars) return undefined;

      return ({
        // minWidth: `${widthChars}ch`,
        width: `${widthChars}ch`,
      });
    },
    [widthChars],
  );

  const thisOnChange = useCallback(
    (event) => {
      if (onChange) {
        const retEvent = { ...event };

        // Unify apostrophes between iOS and Android
        //
        if (event.currentTarget.value?.length) {
          retEvent.currentTarget.value = normalizeTypedText(retEvent.currentTarget.value);
        }

        return onChange(retEvent);
      }

      return null;
    },
    [onChange],
  );

  const thisOnChangePwd = useCallback(
    (event) => {
      if (onChange) {
        const retEvent = { ...event };

        // Use the external id field
        retEvent.currentTarget.id = retEvent.currentTarget.id.replace('_show', '');

        // Unify apostrophes between iOS and Android
        //
        if (event.currentTarget.value?.length) {
          retEvent.currentTarget.value = retEvent.currentTarget.value.replace('’', '\'');
        }

        return onChange(retEvent);
      }

      return null;
    },
    [onChange],
  );

  const thisOnBlur = useCallback(
    () => {
      if (onBlur) onBlur();

      if (onFocusUnfocus) {
        onFocusUnfocus({
          focus: false,
          id,
        });
      }
    },
    [onBlur, onFocusUnfocus, id],
  );

  useEffect(
    () => {
      isMountedRef.current = true;

      return () => {
        isMountedRef.current = false;
      };
    },
    [],
  );

  const thisOnFocus = useCallback(
    () => {
      if (onFocusUnfocus) {
        onFocusUnfocus({
          focus: true,
          id,
        });
      }

      if (narrow && (!noScrollIntoView)) {
        inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(
          () => {
            if (isMountedRef.current) {
              inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          },
          keyboardSettleMs,
        );
      }
    },
    [onFocusUnfocus, id, narrow, noScrollIntoView],
  );

  const thisOnKeyPress = useCallback(
    (event) => { if (onKeyPress) onKeyPress(event); },
    [onKeyPress],
  );

  const thisOnKeyDown = useCallback(
    (event) => { if (onKeyDown) onKeyDown(event); },
    [onKeyDown],
  );

  useEffect(
    () => {
      const currentRef = (showPassword || (type !== 'password'))
        ? inputRef.current
        : inputShowPwdRef.current;

      if (focus && currentRef) {
        currentRef.focus();
      }
    },
    [focus, showPassword, id, type],
  );

  const internal = useMemo(
    () => {
      if (!multiline) {
        const prefixClassName = classNames(
          'whitespace-pre text-nowrap cursor-text pl-2',
          {
            'text-black': (!invalid),
            'text-pxUiAlert': invalid,
            'opacity-50': !disabled,
          },
        );

        const clickPrefix = () => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(0, 0);
            inputRef.current.focus();
          }
        };

        return (
          <>
            {(nonEditablePrefix?.length)
              ? (
                  <button
                    className={prefixClassName}
                    type="button"
                    disabled={disabled}
                    id={`prefix_${id}`}
                    onClick={clickPrefix}
                    // Make sure this button is not focusable via keyboard navigation:
                    tabIndex="-1"
                  >
                    {nonEditablePrefix}
                  </button>
                )
              : null}

            <input
              className={txtClassName}
              onChange={thisOnChange}
              onFocus={thisOnFocus}
              onBlur={thisOnBlur}
              onKeyPress={thisOnKeyPress} // NOSONAR backward compatibility
              onKeyDown={thisOnKeyDown}
              value={value}
              placeholder={placeholder}
              id={id}
              name={name}
              autoComplete={autoComplete}
              type={type}
              ref={inputRef}
              style={txtStyle}
              disabled={disabled}
              hidden={showPassword}
              spellCheck={spellCheck}
            />

            {
              (type === 'password')
                ? (
                  // Simple text field, to show password when needed,
                  // but keep editing cursor in the same place
                    <input
                      className={txtClassName}
                      onChange={thisOnChangePwd}
                      onFocus={thisOnFocus}
                      onBlur={thisOnBlur}
                      onKeyPress={thisOnKeyPress} // NOSONAR backward compatibility
                      onKeyDown={thisOnKeyDown}
                      value={value}
                      placeholder={placeholder}
                      id={`${id}_show`}
                      name={name}
                      autoComplete={autoComplete}
                      type="text"
                      ref={inputShowPwdRef}
                      style={txtStyle}
                      disabled={disabled}
                      hidden={showPassword ? undefined : true}
                    />
                  )
                : null
            }
          </>
        );
      }

      return (
        <textarea
          className={txtClassName}
          onChange={thisOnChange}
          onFocus={thisOnFocus}
          onBlur={thisOnBlur}
          onKeyPress={thisOnKeyPress} // NOSONAR backward compatibility
          onKeyDown={thisOnKeyDown}
          value={value}
          placeholder={placeholder}
          id={id}
          name={name}
          autoComplete={autoComplete}
          // type={type}
          ref={inputRef}
          style={txtStyle}
          disabled={disabled}
        />
      );
    },
    [
      id, multiline, placeholder, thisOnChange, thisOnKeyPress,
      txtClassName, value, name, autoComplete, type, thisOnBlur,
      thisOnKeyDown, txtStyle, disabled, showPassword,
      thisOnChangePwd, nonEditablePrefix, invalid, thisOnFocus, spellCheck,
    ],
  );

  return (
    <div
      className={divClassName}
      style={style}
      key={id}
    >
      { ((value?.length) || (!nullText))
        ? null
        : (
            <div
              className="pointer-events-none absolute top-0 left-1 w-full h-full flex flex-row items-center justify-start outline-none resize-none overflow-hidden bg-transparent italic"
            >
              <span>
                {nullText}
              </span>
            </div>
          )}

      {internal}

      { (value?.length && (!hideLabel))
        ? (
            <label htmlFor={id} className={labelClassName}>
              {placeholder}
              {(required)
                ? <span className="ml-1">(*)</span>
                : null}
            </label>
          )
        : null}

      { ((!(value?.length)) && required)
        ? (
            <div className="absolute -top-2 left-2 text-pxUiAlert px-1 text-xs bg-white text-nowrap">
              {translate('required')}
            </div>
          )
        : null}

      { ((!(value?.length)) && optional)
        ? (
            <div className={labelClassName}>
              {translate('optional')}
            </div>
          )
        : null}
    </div>
  );
};

TextInput.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({ }),
  id: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  multiline: PropTypes.bool,
  onChange: PropTypes.func,
  onKeyPress: PropTypes.func,
  // Catch keyboard events including escape key:
  onKeyDown: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  hideLabel: PropTypes.bool,
  fullWidth: PropTypes.bool,
  fillType: PropTypes.string,
  autoFill: PropTypes.bool,
  invalid: PropTypes.bool,
  nullText: PropTypes.string,
  // Set to get keyboard focus initially:
  focus: PropTypes.bool,
  widthChars: PropTypes.number,
  showPassword: PropTypes.bool,
  disabled: PropTypes.bool,
  optional: PropTypes.bool,
  required: PropTypes.bool,
  nonEditablePrefix: PropTypes.string,
  onFocusUnfocus: PropTypes.func,
  customTxtColor: PropTypes.bool,
  customMaxHeight: PropTypes.bool,
  spellCheck: PropTypes.bool,
  // Opt out of the phone keyboard's bring-the-field-into-view scroll
  noScrollIntoView: PropTypes.bool,
};

export default TextInput;
